import React, { useState, useEffect } from 'react';
import { Card, Col, Form, Row, Radio, Alert } from 'antd';
import { useParams } from 'react-router-dom';
import { BankOutlined, CreditCardOutlined, AlipayOutlined } from '@ant-design/icons';
import GrabPayLogo from 'images/logo-grabpay.png';

import FormHCaptcha from 'components/HCaptcha/FormHCaptcha/FormHCaptcha';
import FormInput from 'components/Input/FormInput/FormInput';
import FormRadio from 'components/Radio/FormRadio/FormRadio';

import { LABEL_SIZE_LARGE, SPECIAL_HOST_IDS } from 'utils/constants';
import { isStripePaymentMethodAllowed } from 'utils/currency';
import { useHostCurrency } from 'hooks/useDisplayPrice';

import {
  ButtonContainer,
  InputContainer,
  PaymentTypeRadio,
  PaymentTypeRadioIcon,
  PaymentTypeRadioText,
  StyledTextTitle,
  StyledBookingImage
} from './GuestDetailsModal.styles';

const GuestDetailsModal = ({
  form,
  hostStripeConnectId,
  onSubmit,
  isSkipStripePayment,
  roomData,
  hasGenderRestrictions,
  setHasGenderRestrictions
}) => {
  const { hostId } = useParams();
  const currency = useHostCurrency();
  const isSpecialHost = SPECIAL_HOST_IDS.includes(hostId) && roomData.length !== 0;
  const sellingMode = roomData?.[0]?.sellingMode;
  const sellMode = sellingMode === 'male' || sellingMode === 'female' ? sellingMode : undefined;
  const [identity, setIdentity] = useState('student');
  const [gender, setGender] = useState(sellMode);

  useEffect(() => {
    setGender(sellMode);
    form.setFieldsValue({
      identity: identity,
      gender: sellMode
    });
  }, [sellMode, identity, form]);

  const handleVerificationSuccess = hCaptchaToken => {
    form.setFieldsValue({ hCaptchaToken });
  };

  const handleIdentityChange = e => {
    setIdentity(e.target.value);
  };

  const handleGenderChange = e => {
    setGender(e.target.value);
  };

  const checkGenderRestrictions = () => {
    const conflictingRooms = roomData.filter(room => {
      const roomGender = room.sellingMode;
      return (roomGender === 'male' && gender === 'female') || (roomGender === 'female' && gender === 'male');
    });

    const hasRestrictions = conflictingRooms.length > 0;
    setHasGenderRestrictions(hasRestrictions);

    if (hasRestrictions) {
      return (
        <InputContainer>
          <Alert message="You have selected rooms with conflicting gender restrictions." type="error" showIcon />
        </InputContainer>
      );
    }
    return null;
  };

  return (
    <Card title={<StyledTextTitle>Guest Information</StyledTextTitle>}>
      {isSpecialHost && checkGenderRestrictions()}
      {gender != undefined && sellingMode != 'normal' && !hasGenderRestrictions && (
        <InputContainer>
          <Alert message={<span>This room is for {gender}s only</span>} type="warning" showIcon />
        </InputContainer>
      )}
      <InputContainer>
        <Row gutter={8}>
          <Col span={12}>
            <FormInput
              form={form}
              name="firstName"
              label="First Name"
              labelSize={LABEL_SIZE_LARGE}
              placeholder="Enter your first name"
              requiredErrorMessage="Please enter your first name"
            />
          </Col>
          <Col span={12}>
            <FormInput
              form={form}
              name="lastName"
              label="Last Name"
              labelSize={LABEL_SIZE_LARGE}
              placeholder="Enter your last name"
              requiredErrorMessage="Please enter your last name"
            />
          </Col>
        </Row>
      </InputContainer>
      <InputContainer>
        <FormInput
          form={form}
          name="icNo"
          label="IC/Passport No."
          labelSize={LABEL_SIZE_LARGE}
          placeholder="Enter your IC/Passport No."
          requiredErrorMessage="Please enter your IC/Passport No"
        />
      </InputContainer>
      {isSpecialHost && (
        <Form.Item name="gender" label="Gender" rules={[{ required: true, message: 'Please select your gender!' }]}>
          <Radio.Group defaultValue={gender} buttonStyle="solid" onChange={handleGenderChange}>
            <Radio.Button value="male" disabled={sellingMode != 'normal' && gender === 'female'}>
              Male
            </Radio.Button>
            <Radio.Button value="female" disabled={sellingMode != 'normal' && gender === 'male'}>
              Female
            </Radio.Button>
          </Radio.Group>
        </Form.Item>
      )}
      <InputContainer>
        <FormInput
          form={form}
          name="email"
          label="Email"
          labelSize={LABEL_SIZE_LARGE}
          placeholder="Enter your email address"
          requiredErrorMessage="Please enter your email address"
          isEmail
        />
      </InputContainer>
      <InputContainer>
        <FormInput
          form={form}
          name="contactNo"
          label="Contact No."
          labelSize={LABEL_SIZE_LARGE}
          placeholder="Enter your contact number"
          requiredErrorMessage="Please enter your contact number"
          isContactNumber
        />
      </InputContainer>
      {isSpecialHost && (
        <>
          <Form.Item name="identity" label="Identity" rules={[{ required: true, message: 'Please select your identity!' }]}>
            <Radio.Group defaultValue={identity} buttonStyle="solid" onChange={handleIdentityChange}>
              <Radio.Button value="student">Student</Radio.Button>
              <Radio.Button value="staff">Staff</Radio.Button>
            </Radio.Group>
          </Form.Item>
          {identity === 'student' && (
            <InputContainer>
              <FormInput
                name="studentId"
                label="Student ID. (** IF STUDENT HAVE NOT RECEIVE STUDENT ID YET, KINDLY FILL IN 'NA' **)"
                labelSize={LABEL_SIZE_LARGE}
                placeholder="Student ID."
                requiredErrorMessage="Please enter your student ID"
              />
            </InputContainer>
          )}
          {identity === 'staff' && (
            <InputContainer>
              <FormInput
                name="staffId"
                label="Staff ID."
                labelSize={LABEL_SIZE_LARGE}
                placeholder="Staff ID."
                requiredErrorMessage="Please enter your staff ID"
              />
            </InputContainer>
          )}
        </>
      )}
      {false && <FormHCaptcha name="hCaptchaToken" requiredErrorMessage="Please complete the captcha" onVerify={handleVerificationSuccess} />}
      {hostStripeConnectId && !isSkipStripePayment && (
        <FormRadio
          form={form}
          name="paymentMethod"
          label="Select your payment method"
          labelSize={LABEL_SIZE_LARGE}
          selections={[
            {
              value: 'card',
              label: (
                <PaymentTypeRadio>
                  <PaymentTypeRadioIcon>
                    <BankOutlined />
                  </PaymentTypeRadioIcon>
                  <PaymentTypeRadioText>credit card</PaymentTypeRadioText>
                </PaymentTypeRadio>
              )
            },
            {
              value: 'fpx',
              label: (
                <PaymentTypeRadio>
                  <PaymentTypeRadioIcon>
                    <CreditCardOutlined />
                  </PaymentTypeRadioIcon>
                  <PaymentTypeRadioText>fpx</PaymentTypeRadioText>
                </PaymentTypeRadio>
              )
            },
            {
              value: 'alipay',
              label: (
                <PaymentTypeRadio>
                  <PaymentTypeRadioIcon>
                    <AlipayOutlined />
                  </PaymentTypeRadioIcon>
                  <PaymentTypeRadioText>Alipay</PaymentTypeRadioText>
                </PaymentTypeRadio>
              )
            },
            {
              value: 'grabpay',
              label: (
                <PaymentTypeRadio>
                  <PaymentTypeRadioIcon>
                    <StyledBookingImage src={GrabPayLogo} alt={'grabpay'} />
                  </PaymentTypeRadioIcon>
                  <PaymentTypeRadioText>GrabPay</PaymentTypeRadioText>
                </PaymentTypeRadio>
              )
            }
          ].filter(selection => isStripePaymentMethodAllowed(selection.value, currency))}
          type="button"
          requiredErrorMessage="Please select a payment type"
        />
      )}
      <ButtonContainer>{/* <SubmitButton type="primary" htmlType="submit">
            checkout
          </SubmitButton> */}</ButtonContainer>
      {/* </Form> */}
    </Card>
  );
};

export default GuestDetailsModal;
